// Packages
import {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useRecoilValue, useRecoilState } from 'recoil'
import debounce from 'lodash.debounce'
import Shuffle from 'shufflejs'

// Store
import {
  cityId,
  countryProps,
  cuisineId,
  filters,
  merchantsData,
  modalMerchants,
  showMerchantsModal,
  modalMerchant,
  filterHalal,
  searchQuery,
  sortMethod,
  userCoordinates,
} from '../../Store/Store'

// Context
import { Language } from '../../Language/Language'

// Helpers
import {
  parseCoordinatesFromUrl,
  calculateDistance,
  formatDistance,
} from './distance.helpers'

// Elements
import {
  SuspenseContainer,
  Container,
  ErrorMsg,
  Card,
  CardBody,
  CardBodyGrouped,
  CardFooter,
  Title,
  Address,
  Detail,
  MapLink,
  Instruction,
  Button,
  ModalButton,
  ButtonDisabled,
  New,
  SpecialOffer,
  DistanceBadge,
  Flag,
  MidFlags,
  AddressSection,
  CuisineRow,
  CuisineItem,
  HotelRestaurantStar,
  Flags,
} from './Results.elements'

import {
  ModalMask,
  ModalContainer,
  Cross,
  RestaurantBanner,
  CloseBanner,
  CloseButton,
  SpecialOfferWrapper,
} from './Modal.elements'

import {
  IconCuisine,
  IconTakeout,
  IconPin,
  IconExternalLink,
  IconSpecialOffer,
  IconTelephone,
  IconSpecialOfferTitle,
} from './Results.icons'

import { setCardWidth } from './Listing.helpers'

import auMerchantOverrides from './auMerchantOverides'

function MerchantsSuspense() {
  const arr = [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1]

  return (
    <SuspenseContainer>
      {arr.map((_, index) => (
        <div key={index} className="col-6@xs col-6@sm col-4@md">
          <Card isGhost>
            <CardBody>
              <Title>A</Title>

              <Address>
                A<br />A
              </Address>

              <Detail>
                <a href="/">A</a>
                <IconPin className="ml-2" />
              </Detail>

              <Detail>
                <IconCuisine className="mr-2" />A
              </Detail>
            </CardBody>
            <CardFooter>
              <Button href="/">A</Button>
            </CardFooter>
          </Card>
        </div>
      ))}
    </SuspenseContainer>
  )
}

function Merchants({ filterCallback, locale }) {
  const { results: copy } = useContext(Language)
  /* eslint-disable no-unused-vars */
  const [groupedMerchants, setGroupedMerchants] = useRecoilState(modalMerchants)
  const [showModal, setShowModal] = useRecoilState(showMerchantsModal)
  const [filterHalalMerchants] = useRecoilState(filterHalal)

  const [groupMerchant, setGroupMerchant] = useRecoilState(modalMerchant)
  /* eslint-enable no-unused-vars */

  const { country } = useRecoilValue(filters)
  const merchants = useRecoilValue(merchantsData(locale))

  const props = useRecoilValue(countryProps)
  const selectedCity = useRecoilValue(cityId)
  const selectedCuisine = useRecoilValue(cuisineId)
  const query = useRecoilValue(searchQuery)
  const sort = useRecoilValue(sortMethod)
  const coordinates = useRecoilValue(userCoordinates)

  const setShowFilterMsg = (value, isSearchMode = false) => {
    filterCallback(value, isSearchMode)
  }

  const [filtered, setFiltered] = useState([])
  const [takeoutFlag, setTakeoutFlag] = useState(false)
  const [shuffler, setShuffler] = useState(null)
  const [isSearchActive, setIsSearchActive] = useState(false)

  // HK restaurant names can be 2 characters (Chinese), so allow shorter queries there.
  const minSearchLength = country === 'HK' ? 2 : 3

  // The current Shuffle filter predicate (by selected city/cuisine). Held in a
  // ref so the various resetItems() callbacks can re-apply it after resetting —
  // resetItems() otherwise clears the active filter and shows everything again.
  const filterPredicate = useMemo(() => {
    const hasActiveFilters =
      (selectedCity && selectedCity.length) ||
      (selectedCuisine && selectedCuisine.length)
    if (!hasActiveFilters) return () => true
    // Keep a card if it matches the selected city AND cuisine. Group listings
    // are always kept — their inner city/cuisine filtering happens at render.
    return (element) => {
      const groups = JSON.parse(element.getAttribute('data-groups'))
      if (!groups.includes('groupListing')) {
        if (
          (selectedCity && !groups.includes(selectedCity)) ||
          (selectedCuisine && !groups.includes(selectedCuisine))
        ) {
          return false
        }
      }
      return true
    }
  }, [selectedCity, selectedCuisine])
  const filterPredicateRef = useRef(filterPredicate)
  filterPredicateRef.current = filterPredicate
  // Guards against the shuffler being initialised twice when the two effects
  // below both see shuffler === null in the same commit.
  const shufflerInitRef = useRef(false)

  function checkTakeoutFlag() {
    return props.isTakeoutAvailable ?? false
  }

  // Helper function to calculate merchant distance
  function getMerchantDistance(merchant) {
    if (!coordinates) return null

    // Check if merchant has lat/lng from API
    if (merchant.lat != null && merchant.lng != null) {
      const distance = calculateDistance(
        coordinates.lat,
        coordinates.lng,
        merchant.lat,
        merchant.lng,
      )

      return distance
    }

    // Fallback: try to parse from googleMapsUrl
    const merchantCoords = parseCoordinatesFromUrl(merchant.googleMapsUrl)
    if (!merchantCoords) {
      return null
    }

    return calculateDistance(
      coordinates.lat,
      coordinates.lng,
      merchantCoords.lat,
      merchantCoords.lng,
    )
  }

  // Helper function to flatten merchant groups into individual merchants
  function flattenMerchantGroups(merchantsList) {
    const flattened = []

    merchantsList.forEach((merchant) => {
      if (
        merchant.isMerchantGroup &&
        merchant.merchants &&
        merchant.merchants.length > 0
      ) {
        // Add all child merchants individually, marking them as formerly grouped
        merchant.merchants.forEach((childMerchant) => {
          flattened.push({
            ...childMerchant,
            _isFromGroup: true,
            _groupName: merchant.name,
          })
        })
      } else {
        // Add regular merchant as-is
        flattened.push(merchant)
      }
    })

    return flattened
  }

  // Helper function to sort merchants
  function sortMerchants(merchantsList) {
    if (sort === 'distance' && coordinates) {
      // Flatten merchant groups for distance sorting
      const flattenedList = flattenMerchantGroups(merchantsList)

      // Create a copy with distances calculated
      const merchantsWithDistance = flattenedList.map((merchant) => ({
        ...merchant,
        _distance: getMerchantDistance(merchant),
      }))

      // Sort by distance (null distances pushed to end)
      // Note: Merchants without lat/lng coordinates will appear at the end
      const sorted = merchantsWithDistance.sort((a, b) => {
        if (a._distance === null) return 1
        if (b._distance === null) return -1
        return a._distance - b._distance
      })

      return sorted
    }

    // Default alphabetical sort (existing behavior with grouped merchants)
    return merchantsList
  }

  useEffect(() => {
    if (!merchants || !country) return

    let processedMerchants = merchants

    // Apply halal filter first
    if (filterHalalMerchants) {
      const f = merchants.filter((merchant) => {
        if (merchant.isHalal) {
          return merchant
        }
        return false
      })

      processedMerchants = f.map((m) => {
        if (m.merchants.length) {
          const filteredNestedMerchants = m.merchants.filter(
            (nestedMerchant) => nestedMerchant.isHalal,
          )
          return filteredNestedMerchants.length
            ? { ...m, merchants: filteredNestedMerchants }
            : m
        }
        return m
      })
    }

    // Apply search filter
    const searchActive = query && query.length >= minSearchLength
    setIsSearchActive(searchActive)

    if (searchActive) {
      const searchLower = query.toLowerCase()
      const flattenedMerchants = []

      processedMerchants.forEach((merchant) => {
        // Check if it's a merchant group
        if (
          merchant.isMerchantGroup &&
          merchant.merchants &&
          merchant.merchants.length > 0
        ) {
          // Search through individual merchants in the group
          merchant.merchants.forEach((subMerchant) => {
            const subName =
              subMerchant.translations?.[locale.replace('-', '_')]?.name ||
              subMerchant.translations?.en?.name ||
              subMerchant.name ||
              ''

            if (subName.toLowerCase().includes(searchLower)) {
              // Add individual merchant from group
              flattenedMerchants.push({
                ...subMerchant,
                isMerchantGroup: false,
                merchants: [],
              })
            }
          })
        } else {
          // Check single merchant
          const merchantName =
            merchant.translations?.[locale.replace('-', '_')]?.name ||
            merchant.translations?.en?.name ||
            merchant.name ||
            ''

          if (merchantName.toLowerCase().includes(searchLower)) {
            flattenedMerchants.push(merchant)
          }
        }
      })

      setFiltered(sortMerchants(flattenedMerchants))
    } else {
      setFiltered(sortMerchants(processedMerchants))
    }

    setTakeoutFlag(checkTakeoutFlag(country))
  }, [
    merchants,
    country,
    filterHalalMerchants,
    query,
    locale,
    sort,
    coordinates,
  ])

  useEffect(() => {
    let debFunc = debounce(() => {
      handleResize()
    }, 50)

    handleResize()

    window.addEventListener('resize', debFunc)

    return () => {
      if (debFunc) {
        window.removeEventListener('resize', debFunc)
      }
    }
  }, [])

  // const embedWidget = (merchantId, venueId) => {
  //   if (document.getElementById(merchantId)) {
  //     window.resyWidget.addButton(document.getElementById(merchantId), {
  //       venueId: venueId,
  //       apiKey: " 20pPy93nbRe6xwdXpdes9v5kyCJ0D5z1", // TODO Set as env var?
  //       replace: true,
  //       seats: "2",
  //     });
  //   }
  // };

  // const resetResyWidgets = () => {
  //   if (window.resyWidget) {
  //     merchants.forEach((merchant) => {
  //       if (merchant.isMerchantGroup && merchant.merchants.length) {
  //         merchant.merchants.forEach((groupMerchant) => {
  //           const { resyEnabled, resyVenueId } = groupMerchant.businessData;
  //           if (resyEnabled && resyVenueId) {
  //             embedWidget(groupMerchant.id, resyVenueId);
  //           }
  //         });
  //       } else {
  //         const { resyEnabled, resyVenueId } = merchant.businessData;
  //         if (resyEnabled && resyVenueId) {
  //           embedWidget(merchant.id, resyVenueId);
  //         }
  //       }
  //     });
  //   }
  // };

  useEffect(() => {
    if (merchants && merchants.length) {
      if (!shuffler) {
        if (!shufflerInitRef.current) {
          shufflerInitRef.current = true
          const instance = addShuffler()
          // Equalize heights before Shuffle's first layout pass sees them,
          // so a card with an extra flag row doesn't get a shorter neighbour
          // positioned over its footer/CTA.
          setTimeout(() => {
            setEqualHeightForShuffleItems()
            instance.update()
          }, 0)
        }
      } else {
        setTimeout(() => {
          setEqualHeightForShuffleItems()
        }, 0)
        setTimeout(() => {
          shuffler.resetItems()
          // Re-apply the active city/cuisine filter — resetItems() clears it.
          shuffler.filter(filterPredicateRef.current)
        }, 500)
      }
    }
  }, [merchants, filtered, selectedCity, selectedCuisine, filterHalalMerchants])

  useEffect(() => {
    if (showModal) {
      setTimeout(() => {
        // resetResyWidgets();
        const modal = document.getElementById('modal')
        modal.setAttribute('tabindex', 0)
        modal.focus()
      }, 500)
    }
  }, [showModal])

  useEffect(() => {
    if (shuffler && shuffler.resetItems) {
      shuffler.resetItems()
      // Re-apply the active city/cuisine filter — resetItems() clears it.
      if (shuffler.filter) {
        shuffler.filter(filterPredicateRef.current)
      }
    }
  }, [shuffler])

  useEffect(() => {
    if (shuffler && shuffler.filter) {
      const searchActive = query && query.length >= minSearchLength

      // Whether a city/cuisine dropdown filter is currently applied
      const hasActiveFilters =
        (selectedCity && selectedCity.length) ||
        (selectedCuisine && selectedCuisine.length)

      if (searchActive) {
        // Search is active: `filtered` already holds the name matches. Combine
        // with the dropdowns by refining those matches by city/cuisine too
        // (filterPredicate is a no-op when no city/cuisine is selected).
        shuffler.filter(filterPredicate)

        // No results if there were no name matches, or every match was filtered
        // out by the city/cuisine selection.
        const checkNoResults = () => {
          const hiddenItems = document.querySelectorAll('.shuffle-item--hidden')
          const allItems = document.querySelectorAll('.shuffle-item')
          const noResults =
            filtered.length === 0 ||
            (allItems.length > 0 && allItems.length === hiddenItems.length)
          setShowFilterMsg(noResults, true)
        }

        checkNoResults()
        setTimeout(checkNoResults, 350)
      } else {
        // When search is cleared, reset items to restore original order, then
        // re-apply any active city/cuisine filter (resetItems clears it).
        if (
          query !== undefined &&
          query.length < minSearchLength &&
          isSearchActive
        ) {
          setTimeout(() => {
            shuffler.resetItems()
            shuffler.filter(filterPredicateRef.current)
          }, 0)
        }

        if (hasActiveFilters) {
          // Apply city/cuisine filters when search is not active
          shuffler.filter(filterPredicate)

          setTimeout(() => {
            const hiddenItems = document.querySelectorAll(
              '.shuffle-item--hidden',
            )
            const allItems = document.querySelectorAll('.shuffle-item')
            const noResults =
              hiddenItems.length > 0 && allItems.length === hiddenItems.length
            setShowFilterMsg(noResults, false)
          }, 350)
        } else {
          // No filters active, show all items and hide message
          shuffler.filter(() => true)
          setShowFilterMsg(false, false)
        }
      }
    }
  }, [
    selectedCity,
    selectedCuisine,
    query,
    filtered,
    isSearchActive,
    filterPredicate,
  ])

  useEffect(() => {
    if (shuffler) {
      shuffler.on(Shuffle.EventType.LAYOUT, () => {
        document.getElementById('shuffleWrapper').style.overflow = 'visible'
      })
    }
  }, [shuffler])

  function addShuffler() {
    const instance = new Shuffle(document.getElementById('shuffleWrapper'), {
      itemSelector: '.shuffle-card',
      sizer: document.getElementById('shuffleSizer'),
      filterMode: Shuffle.FilterMode.ALL,
      speed: 275,
    })
    setShuffler(instance)
    return instance
  }

  function updateShuffle() {
    if (shuffler && shuffler.update) {
      setTimeout(() => {
        setEqualHeightForShuffleItems()
      }, 0)
      setTimeout(() => {
        shuffler.update()
      }, 500)
    }
  }

  function handleResize() {
    updateShuffle()
  }

  function setEqualHeightForShuffleItems() {
    // Cards hidden by the active Shuffle filter still match .shuffle-card and
    // report a 0 offsetHeight — excluding them keeps a filtered-out card from
    // silently capping maxHeight once it's filtered back in.
    const elements = Array.from(
      document.querySelectorAll(
        '#shuffleWrapper .shuffle-card:not(.shuffle-item--hidden)',
      ),
    )
    if (!elements.length) return

    const cards = elements
      .map((el) => el.querySelector('div'))
      .filter(Boolean)

    // Reset previous overrides so we measure natural heights
    cards.forEach((card) => {
      card.style.height = ''
      card.style.minHeight = ''
    })
    elements.forEach((el) => {
      const address = el.querySelector('[data-address]')
      if (address) address.style.minHeight = ''
    })

    // Pin every AddressSection to the tallest one FIRST — before measuring
    // card heights. A card with a short address gains extra height from this
    // pin; measuring card height beforehand ignored that growth, so a card
    // could be capped shorter than it would end up once its address grew,
    // clipping the footer/CTA underneath it.
    const addressSections = elements
      .map((el) => el.querySelector('[data-address]'))
      .filter(Boolean)
    const maxAddressHeight = addressSections.length
      ? Math.max(...addressSections.map((a) => a.offsetHeight))
      : 0
    addressSections.forEach((a) => {
      a.style.minHeight = `${maxAddressHeight}px`
    })

    // Equalize card heights — measure and write the same element (the inner
    // Card), not the .shuffle-card wrapper, so the two can't disagree. Use
    // min-height rather than height: a card should never be capped shorter
    // than the content it actually needs, only stretched taller to match its
    // row — a hard height clips/overlaps the footer if our measurement is
    // ever off by a few px (font metrics, sub-pixel rounding).
    const maxHeight = Math.max(...cards.map((card) => card.offsetHeight))
    cards.forEach((card) => {
      card.style.minHeight = `${maxHeight}px`
    })
  }

  function getTranslation(key, translations) {
    const market = locale.replace('-', '_')
    if (translations[market] && translations[market][key]) {
      return translations[market][key]
    }
    // Fall back to "en" value...
    return translations['en'][key]
  }

  function getBusinessData(key, translations) {
    const market = locale.replace('-', '_')
    if (translations[key] && translations[key][market]) {
      return translations[key][market]
    }
    // Fall back to "en" value...
    return translations[key]['en']
  }

  function translate(item, translations) {
    // Fall back to "en" value...
    const market = locale.replace('-', '_')
    if (!item || !item.hasOwnProperty('translations')) {
      throw Error(`Translations object not in ${JSON.stringify(item)}`)
    }
    const f = item.translations[market] || item.translations['en']
    return f.title
  }

  function findMerchantGroup(id, filteredMerchants = []) {
    const groupMerchant = merchants.find((merchant) => {
      return merchant.id === id
    })
    // console.log(groupMerchant.merchants, filteredMerchants);
    if (filteredMerchants.length) {
      const filtered = groupMerchant.merchants.filter((item) =>
        filteredMerchants.includes(item.id),
      )
      setGroupedMerchants(filtered)
    } else {
      setGroupedMerchants(groupMerchant.merchants)
    }
    setGroupMerchant(groupMerchant)
    handleTrackEvent('click', `openGroupModal${groupMerchant.name}`)
    setShowModal(true)
  }

  function isCountryBelguimOrNetherlands() {
    if (country === 'BE' || country === 'NL') {
      return true
    }
    return false
  }

  const [currentModal, setCurrentModal] = useState(null)

  const handleOpenModal = (modalId) => setCurrentModal(modalId)
  const handleCloseModal = () => setCurrentModal(null)

  const handleTrackEvent = (action, asset) => {
    window.tuiTrack.trackEvent(action, asset)
  }

  const handleMakeBooking = (website, name) => {
    window.tuiTrack.trackEvent('click', `makeBookingClick:${name}`)
    window.open(website, '_blank')
  }

  return (
    <>
      {filtered.map(
        ({
          id,
          name,
          address,
          cuisine,
          city,
          postcode,
          businessData,
          translations,
          googleMapsUrl,
          takeoutAvailable,
          onlineOnly,
          isMerchantGroup,
          merchants,
          isNew,
          isHalal,
          isMichelin,
          allMerchantsNew,
          hasSpecialOffer,
          lat,
          lng,
        }) => {
          const hasMerchants =
            isMerchantGroup && merchants.length && sort !== 'distance'
          const filteredMerchants = []
          const cuisines = []
          const postcodeFirst = isCountryBelguimOrNetherlands()

          if (hasMerchants) {
            merchants.forEach((item) => {
              let matchCity = false
              let matchCuisine = false
              let newCuisine = false
              if (
                (selectedCity && item.city.id === selectedCity) ||
                !selectedCity
              ) {
                matchCity = true
              }
              if (cuisines.length) {
                for (let i = 0; i < cuisines.length; i += 1) {
                  if (cuisines[i].id !== item.cuisine.id) {
                    newCuisine = true
                    break
                  }
                }
              } else {
                newCuisine = true
              }
              if (newCuisine) {
                cuisines.push(item.cuisine)
              }
              if (
                (selectedCuisine && item.cuisine.id === selectedCuisine) ||
                !selectedCuisine
              ) {
                matchCuisine = true
              }
              if (matchCity && matchCuisine) {
                filteredMerchants.push(item.id)
              }
            })

            // console.log(cuisines);
          }

          const hoistedMerchant =
            filteredMerchants.length === 1
              ? merchants.find(
                  (merchant) => merchant.id === filteredMerchants[0],
                )
              : null

          // override parent item params
          if (hoistedMerchant) {
            name = hoistedMerchant.name
            address = hoistedMerchant.address
            cuisine = hoistedMerchant.cuisine
            city = hoistedMerchant.city
            postcode = hoistedMerchant.postcode
            businessData = hoistedMerchant.businessData
            translations = hoistedMerchant.translations
            googleMapsUrl = hoistedMerchant.googleMapsUrl
            takeoutAvailable = hoistedMerchant.takeoutAvailable
            onlineOnly = hoistedMerchant.onlineOnly
            lat = hoistedMerchant.lat
            lng = hoistedMerchant.lng
          }

          const distanceKm = coordinates
            ? getMerchantDistance({ lat, lng, googleMapsUrl })
            : null

          const { website, phone, openingSoon, isInHotel } = businessData
          const hasWebsite = website && website.length
          const hasPhone = phone && phone.length
          const hasHalal = isHalal === true
          const hotelRestaurant = isInHotel === true

          const hasMichelin = isMichelin === true
          const hasAuOverride =
            country === 'AU' && auMerchantOverrides.includes(id)

          return (
            <div
              key={id}
              className="col-6@xs col-6@sm col-4@md shuffle-card"
              data-groups={`["${city ? city.id : 'no-city'}","${
                cuisine ? cuisine.id : 'no-cuisine'
              }","${hasMerchants && filteredMerchants.length ? 'groupListing' : 'not-grouped'}"]`}
            >
              {(!hasMerchants || hoistedMerchant) && (
                <Card>
                  <CardBody>
                    <Title>
                      <Flags id="flags">
                        {businessData.isNew && <New>{copy.new}</New>}
                      </Flags>
                      {getTranslation('name', translations)}
                      {hotelRestaurant && (
                        <HotelRestaurantStar>*</HotelRestaurantStar>
                      )}
                    </Title>

                    <AddressSection data-address>
                      {!onlineOnly && city && !postcodeFirst && (
                        <Address>
                          {getTranslation('address', translations)}
                          <br />
                          {translate(city)}
                          <br />
                          {getTranslation('postcode', translations)}
                        </Address>
                      )}

                      {!onlineOnly && city && postcodeFirst && (
                        <Address>
                          {getTranslation('address', translations)}
                          <br />
                          {getTranslation('postcode', translations)}
                          <br />
                          {translate(city)}
                        </Address>
                      )}

                      {onlineOnly && !address && (
                        <Address>{copy.onlineOnly}</Address>
                      )}

                      {onlineOnly && address && !city && (
                        <Address>
                          {getTranslation('address', translations)}
                        </Address>
                      )}

                      {onlineOnly && address && city && (
                        <Address>
                          {getTranslation('address', translations)}
                          <br />
                          {translate(city)}
                        </Address>
                      )}

                      {hasAuOverride && (
                        <div>
                          Restaurant participation temporarily suspended
                        </div>
                      )}

                      {!hasAuOverride && takeoutFlag && takeoutAvailable && (
                        <Detail>
                          <IconTakeout className="mr-2" />
                          {copy[takeoutAvailable]}
                        </Detail>
                      )}
                    </AddressSection>

                    {!hasAuOverride && cuisine && (
                      <CuisineRow>
                        <CuisineItem>
                          <IconCuisine className="mr-2" />
                          {translate(cuisine)}
                        </CuisineItem>
                      </CuisineRow>
                    )}

                    {(hasSpecialOffer ||
                      hasHalal ||
                      hasMichelin ||
                      distanceKm !== null) && (
                      <MidFlags>
                        {distanceKm !== null && (
                          <DistanceBadge>
                            {formatDistance(distanceKm, locale)}
                          </DistanceBadge>
                        )}
                        {hasSpecialOffer && (
                          <SpecialOffer
                            onClick={() => handleOpenModal(id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <IconSpecialOffer className="mr-2" />
                            {copy.specialOffer || 'View Special Offer'}
                          </SpecialOffer>
                        )}
                        {hasHalal && <Flag>{copy.halal}</Flag>}
                        {hasMichelin && <Flag>{copy.michelin}</Flag>}
                      </MidFlags>
                    )}
                  </CardBody>

                  {hasAuOverride && <CardFooter data-footer />}

                  {!hasAuOverride && (
                    <CardFooter data-footer>
                      {businessData.resyEnabled && businessData.resyVenueId ? (
                        <div id={id}></div>
                      ) : (
                        <>
                          {hasWebsite && (
                            <>
                              {!hasAuOverride &&
                                !onlineOnly &&
                                googleMapsUrl && (
                                  <MapLink>
                                    <a
                                      rel="noopener noreferrer"
                                      target="_blank"
                                      href={googleMapsUrl}
                                    >
                                      {copy.viewmap}
                                    </a>
                                    <IconPin className="ml-2" />
                                  </MapLink>
                                )}
                              {openingSoon ? (
                                <ButtonDisabled>
                                  {copy.openingSoon}
                                  <IconExternalLink className="ml-2" />
                                </ButtonDisabled>
                              ) : (
                                <Button
                                  rel="noopener noreferrer"
                                  target="_blank"
                                  onClick={() =>
                                    handleMakeBooking(
                                      website,
                                      getTranslation('name', translations),
                                    )
                                  }
                                >
                                  {copy.makebooking}
                                  <IconExternalLink className="ml-2" />
                                </Button>
                              )}
                            </>
                          )}

                          {!hasWebsite && hasPhone && (
                            <>
                              {openingSoon && (
                                <Instruction marginBottom={2} weight={500}>
                                  Opening soon
                                </Instruction>
                              )}
                              {!hasAuOverride &&
                                !onlineOnly &&
                                googleMapsUrl && (
                                  <MapLink>
                                    <a
                                      rel="noopener noreferrer"
                                      target="_blank"
                                      href={googleMapsUrl}
                                    >
                                      {copy.viewmap}
                                    </a>
                                    <IconPin className="ml-2" />
                                  </MapLink>
                                )}
                              {openingSoon ? (
                                <ButtonDisabled>
                                  {phone} <IconTelephone className="ml-2" />
                                </ButtonDisabled>
                              ) : (
                                <Button
                                  rel="noopener noreferrer"
                                  href={`tel:${phone}`}
                                >
                                  {phone} <IconTelephone className="ml-2" />
                                </Button>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </CardFooter>
                  )}
                </Card>
              )}

              {hasMerchants && !hoistedMerchant && (
                <Card location>
                  <CardBodyGrouped>
                    <Title>
                      <Flags id="flags">
                        {allMerchantsNew && <New>{copy.new}</New>}
                      </Flags>
                      {getTranslation('name', translations)}
                    </Title>
                    <AddressSection data-address>
                      <MapLink>
                        {filteredMerchants.length} {copy.locations}
                      </MapLink>
                      {hasAuOverride && (
                        <div style={{ marginTop: '1em' }}>
                          Restaurant participation temporarily suspended
                        </div>
                      )}
                    </AddressSection>

                    <CuisineRow>
                      <CuisineItem>
                        <IconCuisine className="mr-2" />{' '}
                        {cuisines.length > 1 ? (
                          <>{copy.groupCuisine}</>
                        ) : (
                          <>{translate(cuisines[0])}</>
                        )}
                      </CuisineItem>
                    </CuisineRow>

                    {(hasHalal || hasMichelin) && (
                      <MidFlags>
                        {hasHalal && <Flag>{copy.halal}</Flag>}
                        {hasMichelin && <Flag>{copy.michelin}</Flag>}
                      </MidFlags>
                    )}
                  </CardBodyGrouped>

                  <CardFooter data-footer>
                    <ModalButton
                      onClick={() => findMerchantGroup(id, filteredMerchants)}
                      onKeyUp={(e) => {
                        if (e.keyCode === 13 || e.keyCode === 32) {
                          findMerchantGroup(id, filteredMerchants)
                          e.stopPropagation()
                        }
                      }}
                    >
                      {copy.button}
                    </ModalButton>
                  </CardFooter>
                </Card>
              )}
            </div>
          )
        },
      )}
      {/* Modal Component Outside of Card */}
      {currentModal && (
        <>
          <ModalMask />
          <ModalContainer
            id="modal"
            tabIndex="0"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            type="specialoffer"
          >
            <RestaurantBanner>
              <IconSpecialOfferTitle className={'mr-2'} />
              {/* Display the business name or special offer title */}
              {getTranslation(
                'name',
                filtered.find((item) => item.id === currentModal)?.translations,
              )}
              <Cross onClick={handleCloseModal} />
            </RestaurantBanner>
            <SpecialOfferWrapper>
              {/* Special offer details */}
              <span
                dangerouslySetInnerHTML={{
                  __html: getBusinessData(
                    'specialOfferDetails',
                    filtered.find((item) => item.id === currentModal)
                      ?.businessData,
                  ),
                }}
              />
            </SpecialOfferWrapper>
            <CloseBanner type="specialoffer">
              <CloseButton
                role="button"
                tabIndex="0"
                onClick={handleCloseModal}
              >
                {copy.close}
              </CloseButton>
            </CloseBanner>
          </ModalContainer>
        </>
      )}
    </>
  )
}

export default function Results({ locale, country, filterHalalMerchants }) {
  const { results: copy } = useContext(Language)
  const [showFilterMsg, setShowFilterMsg] = useState(false)
  const [isSearchMode, setIsSearchMode] = useState(false)
  const filterCallback = (showMsg, searchMode = false) => {
    setShowFilterMsg(showMsg)
    setIsSearchMode(searchMode)
  }

  useEffect(() => {
    setCardWidth()
  }, [])

  return (
    <Container>
      <ErrorMsg style={{ display: showFilterMsg ? 'flex' : 'none' }}>
        {isSearchMode
          ? copy.noSearchResults || copy.notAvailable
          : copy.notAvailable}
      </ErrorMsg>
      <div id="shuffleWrapper" className="row">
        <Suspense fallback={<MerchantsSuspense />}>
          <Merchants
            filterCallback={filterCallback}
            locale={locale}
            country={country}
            filterHalalMerchants={filterHalalMerchants}
          />
        </Suspense>
        <div
          id="shuffleSizer"
          className="col-1@xs col-1@sm col-1@md col-1@lg "
        ></div>
      </div>
    </Container>
  )
}
